const _ = require('underscore/underscore-min');
const Q = require('q');
const reqwest = require('reqwest/reqwest.min');
const endpoints = require('endpoint/endpoint-service.js');
const ExceptionService = require('exceptions/exception-service.js');

/**
 * Returns current user datas
 */
const CurrentUser = (function(root, doc, undefined) {

    'use strict';

    /*
     * Private vars
     */
    let getDeferred = null;
    const filename = 'current-user-service.js';

    const logError = (message, data) => {
        ExceptionService.handle('warning', { filename, message, data });
    };

    /**
     * This methods indicates wether or not a user
     * can be authenticated because all the authentication
     * cookies are present. This attribute is set by PHP in the base Twig.
     */
    const canUserBeAuthenticated = () => {
        return doc.body.getAttribute('data-user-loggedin') || false;
    };

    /**
    * Returns the current user course language code.
    * @param {object} user
    * @param {object} params
    * @param {boolean} params.forceEnc - force 'enc' for english
    * @return {string}
    */
    const getLangCourseCode = (user, params = {}) => {
        let courseCode = (user && user.languages) ? user.languages.learning_default : null;
        if(courseCode === 'en' && params.forceEnc){
            return 'enc';
        }
        return courseCode;
    };

    /**
     * Returns the current authenticated user data
     * @param  {object} params - optional
     * @param  {Boolean} params.force -  whether or not to force the request
     * @return {promise}
     */
    const get = (params = {}) => {

        /**
         * Avoid multiple requests at the same time
         * if the call has been invoked already.
         */
        if (getDeferred && !params.force) {
            return getDeferred.promise;
        }

        // Store defer instance
        getDeferred = Q.defer();

        const onSuccess = (response) => {
            if ((response.status === 'ok') && (response.data)) {
                getDeferred.resolve(response.data);
            } else {
                onError({
                    error: response,
                    reason: 'onSuccess() - bad json format'
                });
            }
        };

        const onError = (error) => {
            logError('get() - Can\'t retrieve user data: ', error);
            getDeferred.reject(error);
        };

        /**
         *
         * In order to avoid multiple calls to the backend
         * for logged out users or guests users, we only try to
         * invoke the user endpoint if we have all the authentication cookies.
         * ('LOGGED_IN' and 'access-token')
         *
         * As this check is twig and cookie based, we must let the force
         * option to bypass this test. Some APIs (e.g. autoauth flow) requires extra call
         * to the user endpoint afterwards and even if the user wasn't authenticated on
         * page load, we might need to force the call (e.g. autoauth flow).
         *
         */
        if (!canUserBeAuthenticated() && !params.force) {
            onError({
                type: 'CURRENT_USER_DENY_AUTHENTICATION',
                message: 'Expected authentication cookies are not present.'
            });
            return getDeferred.promise;
        }

        let _url = endpoints.generateEndpoint('currentUser');
        reqwest({
            url: _url,
            method: 'get',
            type: 'json',
            contentType: 'application/json',
            withCredentials: true
        }).then(onSuccess, onError);

        return getDeferred.promise;

    };

    /**
     * Updates the current authenticated user
     * with given passed data.
     * @param  {object} params
     * @return {promise}
     */
    const update = (params) => {

        const onUpdateError = (e = {}) => {
            logError('update() - Can\'t update user: ', e);
            throw {
                error: e.reason || e,
                type: e.type || 'USER_UPDATE_ERROR'
            };
        };

        const onUpdateSuccess = (response) => {
            if (response && response.data) {
                return response.data;
            }
            throw {
                reason: 'update() - bad json format',
                type: 'BAD_RESPONSE_FORMAT'
            };
        };

        const updateUser = (userId) => {

            if (!params) {
                throw {
                    reason: 'update() - missing params',
                    type: 'MISSING_PARAMS'
                };
            }

            let url = endpoints.generateEndpoint('userUpdate');
            url = url.replace('{uid}', userId);
            return reqwest({
                url: url,
                method: 'put',
                type: 'json',
                contentType: 'application/json',
                withCredentials: true,
                data: JSON.stringify(params)
            });
        };

        return get().then((user) => {
            return user.uid;
        }).then(updateUser).then(onUpdateSuccess).catch(onUpdateError);

    };

    /**
     * Indicates whether or not if
     * the passed user is associated to B2B.
     * Can be Admin, Instructor or Student.
     * @return {Boolean}
     */
    const isB2BUser = (user) => {
        const B2BROLES = [25];
        return _.some(user.roles, (role) => {
            return B2BROLES.indexOf(role) !== -1;
        });
    };

    return {
        get,
        update,
        getLangCourseCode,
        isB2BUser
    };

})(window, document);

module.exports = CurrentUser;
